/**
 * OSINT Store Slice
 *
 * Zustand slice for OSINT tool catalog and session management.
 * Domain: OSINTContext > ToolCatalogAggregate, SessionProvisioningAggregate
 */

import {
  clearError as clearOpError,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { osintApi } from '@/api/osint';
import type { OSINTApplication, OSINTSession, LaunchOSINTResponse } from '@/types/osint.types';

import type { ErrorStates, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface OSINTSlice {
  // State
  osintApplications: OSINTApplication[];
  osintSessions: OSINTSession[];
  osintIsLoading: LoadingStates;
  osintHasLoaded: LoadingStates;
  osintLastLoaded: LoadedTimestamps;
  osintErrors: ErrorStates;

  // Actions
  fetchOSINTApplications: () => Promise<void>;
  fetchOSINTSessions: () => Promise<void>;
  launchOSINTSession: (appId: string, durationMinutes: number) => Promise<LaunchOSINTResponse>;
  terminateOSINTSession: (sessionId: string) => Promise<void>;
  removeOSINTSession: (sessionId: string) => void;
  clearOSINTError: (operation?: string) => void;
  resetOSINT: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialOSINTState = {
  osintApplications: [] as OSINTApplication[],
  osintSessions: [] as OSINTSession[],
  osintIsLoading: {} as LoadingStates,
  osintHasLoaded: {} as LoadingStates,
  osintLastLoaded: {} as LoadedTimestamps,
  osintErrors: {} as ErrorStates,
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createOSINTSlice: StateCreator<OSINTSlice, [], [], OSINTSlice> = (set, get) => ({
  ...initialOSINTState,

  fetchOSINTApplications: async () => {
    const op = 'fetchOSINTApplications';
    const currentState = get();
    if (isOperationLoading(currentState.osintIsLoading, op)) return;

    set({
      osintIsLoading: setLoading(currentState.osintIsLoading, op, true),
      osintErrors: clearOpError(currentState.osintErrors, op),
    });

    try {
      const response = await osintApi.listApplications();
      const state = get();
      const loaded = setLoaded(state.osintHasLoaded, state.osintLastLoaded, op, true);

      set({
        osintApplications: response.applications,
        osintIsLoading: setLoading(state.osintIsLoading, op, false),
        osintHasLoaded: loaded.hasLoaded,
        osintLastLoaded: loaded.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loaded = setLoaded(state.osintHasLoaded, state.osintLastLoaded, op, false);

      set({
        osintIsLoading: setLoading(state.osintIsLoading, op, false),
        osintErrors: setError(
          state.osintErrors,
          op,
          error instanceof Error ? error.message : 'Failed to fetch OSINT applications'
        ),
        osintHasLoaded: loaded.hasLoaded,
        osintLastLoaded: loaded.lastLoaded,
      });
    }
  },

  fetchOSINTSessions: async () => {
    const op = 'fetchOSINTSessions';
    const currentState = get();
    if (isOperationLoading(currentState.osintIsLoading, op)) return;

    set({
      osintIsLoading: setLoading(currentState.osintIsLoading, op, true),
      osintErrors: clearOpError(currentState.osintErrors, op),
    });

    try {
      const response = await osintApi.listSessions();
      const state = get();
      const loaded = setLoaded(state.osintHasLoaded, state.osintLastLoaded, op, true);

      set({
        osintSessions: response.sessions,
        osintIsLoading: setLoading(state.osintIsLoading, op, false),
        osintHasLoaded: loaded.hasLoaded,
        osintLastLoaded: loaded.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loaded = setLoaded(state.osintHasLoaded, state.osintLastLoaded, op, false);

      set({
        osintIsLoading: setLoading(state.osintIsLoading, op, false),
        osintErrors: setError(
          state.osintErrors,
          op,
          error instanceof Error ? error.message : 'Failed to fetch OSINT sessions'
        ),
        osintHasLoaded: loaded.hasLoaded,
        osintLastLoaded: loaded.lastLoaded,
      });
    }
  },

  launchOSINTSession: async (appId: string, durationMinutes: number) => {
    const op = 'launchOSINTSession';

    set({
      osintIsLoading: setLoading(get().osintIsLoading, op, true),
      osintErrors: clearOpError(get().osintErrors, op),
    });

    try {
      const response = await osintApi.launchSession({ appId, durationMinutes });
      set({ osintIsLoading: setLoading(get().osintIsLoading, op, false) });
      return response;
    } catch (error) {
      set({
        osintIsLoading: setLoading(get().osintIsLoading, op, false),
        osintErrors: setError(
          get().osintErrors,
          op,
          error instanceof Error ? error.message : 'Failed to launch OSINT session'
        ),
      });
      throw error;
    }
  },

  terminateOSINTSession: async (sessionId: string) => {
    const op = 'terminateOSINTSession';

    set({
      osintIsLoading: setLoading(get().osintIsLoading, op, true),
      osintErrors: clearOpError(get().osintErrors, op),
    });

    try {
      await osintApi.terminateSession(sessionId);
      const state = get();
      set({
        osintSessions: state.osintSessions.filter((s) => s.sessionId !== sessionId),
        osintIsLoading: setLoading(state.osintIsLoading, op, false),
      });
    } catch (error) {
      set({
        osintIsLoading: setLoading(get().osintIsLoading, op, false),
        osintErrors: setError(
          get().osintErrors,
          op,
          error instanceof Error ? error.message : 'Failed to terminate session'
        ),
      });
      throw error;
    }
  },

  removeOSINTSession: (sessionId: string) => {
    set({
      osintSessions: get().osintSessions.filter((s) => s.sessionId !== sessionId),
    });
  },

  clearOSINTError: (operation?: string) => {
    if (operation) {
      set({ osintErrors: clearOpError(get().osintErrors, operation) });
    } else {
      set({ osintErrors: {} });
    }
  },

  resetOSINT: () => {
    set(initialOSINTState);
  },
});
